import React from "react";
import { Reveal } from "../components/common/Reveal";
import { SectionLabel } from "../components/common/SectionLabel";
import { TechCard } from "../components/common/TechCard";
import { useSection } from "../context/SiteContentContext";
import SEO from "../components/common/SEO";
const DEFAULT_APPS = [
    {
        n: "01",
        title: "Hazard Modeling & Disaster Resilience",
        tag: "Climate",
        body: "Wildfire modeling, flood modeling, levee failure detection, prediction of weak zones. Partnerships include the U.S. Army Corps of Engineers and NASA (Vicksburg).",
    },
];

export default function Applications() {
    const APPS = useSection("applications", DEFAULT_APPS);
    return (
        <>
            <SEO
                title="Applications — Hazard, Utility, Mobility · Skovomax"
                description="Where photonic sensing and GeoAI create decisions: hazard modeling, LiDAR utility risk detection, human-environment interaction, infrastructure optimization and autonomous perception."
            />
            <section className="pt-28 pb-16 border-b border-white/10 relative overflow-hidden">
                <div className="absolute inset-0 photonic-grid opacity-20 pointer-events-none" />
                <div className="relative max-w-[1440px] mx-auto px-6 md:px-10">
                    <Reveal>
                        <SectionLabel label="Applications / 03" />
                        <h1 className="font-serif-display text-6xl md:text-8xl leading-[0.95] tracking-tight max-w-5xl">
                            Where the <br />
                            <span className="italic text-white/70">measurement matters.</span>
                        </h1>
                        <p className="mt-8 max-w-2xl text-white/60 text-lg leading-relaxed">
                            Skovomax platforms are deployed at the intersection of environment, infrastructure,
                            and human systems — where better sensing directly changes outcomes.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="py-24">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {APPS.map((a, i) => (
                            <Reveal delay={i * 80} key={a.title}>
                                <TechCard className="!p-10 h-full">
                                    <div className="flex items-center justify-between mb-6">
                                        <div className="font-mono-tech text-[11px] tracking-[0.22em] text-[var(--accent-indigo)]">
                                            {a.n} / Application
                                        </div>
                                        <div className="chip chip-active">{a.tag}</div>
                                    </div>
                                    <h3 className="font-serif-display text-3xl leading-tight mb-4">{a.title}</h3>
                                    <p className="text-white/60 leading-relaxed">{a.body}</p>
                                </TechCard>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Tech stack */}
            <section className="py-24 border-t border-white/10 bg-[#0a0a18]">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-10">
                    <Reveal>
                        <TechCard className="!p-10 h-full">
                            <SectionLabel label="Data — not generic" />
                            <ul className="mt-6 space-y-3 text-white/70 font-mono-tech text-sm">
                                <li>· LiDAR (airborne, mobile, terrestrial)</li>
                                <li>· Hyperspectral &amp; multispectral imagery</li>
                                <li>· Satellite datasets (Landsat, SAR)</li>
                                <li>· Climate &amp; hazard databases</li>
                                <li>· Socio-economic &amp; mobility data</li>
                            </ul>
                        </TechCard>
                    </Reveal>
                    <Reveal delay={120}>
                        <TechCard className="!p-10 h-full">
                            <SectionLabel label="Methods" />
                            <ul className="mt-6 space-y-3 text-white/70 font-mono-tech text-sm">
                                <li>· Machine Learning (SVM, CNN, ensemble models)</li>
                                <li>· Spatial interaction / gravity models</li>
                                <li>· Morphometric and terrain analysis</li>
                                <li>· Multi-criteria decision analysis (AHP, TOPSIS)</li>
                                <li>· Sensor fusion frameworks</li>
                            </ul>
                        </TechCard>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
